import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

export type CookieCategory = "necessary" | "analytics" | "marketing";

export type CookieConsent = Record<CookieCategory, boolean>;

type StoredConsent = {
  version: 1;
  decidedAt: string;
  consent: CookieConsent;
};

const STORAGE_KEY = "agape.cookieConsent.v1";

const DEFAULT_CONSENT: CookieConsent = {
  necessary: true,
  analytics: false,
  marketing: false,
};

type Ctx = {
  consent: CookieConsent;
  hasDecided: boolean;
  isPreferencesOpen: boolean;
  openPreferences: () => void;
  closePreferences: () => void;
  acceptAll: () => void;
  rejectAll: () => void;
  save: (partial: Partial<Omit<CookieConsent, "necessary">>) => void;
};

const CookieConsentContext = createContext<Ctx | null>(null);

function readStored(): StoredConsent | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredConsent;
    if (parsed?.version !== 1 || !parsed?.consent) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeStored(consent: CookieConsent) {
  try {
    const payload: StoredConsent = {
      version: 1,
      decidedAt: new Date().toISOString(),
      consent: { ...consent, necessary: true },
    };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* ignore quota errors */
  }
}

export function CookieConsentProvider({ children }: { children: React.ReactNode }) {
  const [consent, setConsent] = useState<CookieConsent>(DEFAULT_CONSENT);
  const [hasDecided, setHasDecided] = useState(false);
  const [isPreferencesOpen, setPreferencesOpen] = useState(false);

  useEffect(() => {
    const stored = readStored();
    if (stored) {
      setConsent({ ...stored.consent, necessary: true });
      setHasDecided(true);
    }
  }, []);

  useEffect(() => {
    if (!hasDecided) return;
    window.dispatchEvent(new CustomEvent("cookieconsentchange", { detail: consent }));
  }, [consent, hasDecided]);

  const commit = useCallback((next: CookieConsent) => {
    const sanitized: CookieConsent = { ...next, necessary: true };
    setConsent(sanitized);
    setHasDecided(true);
    writeStored(sanitized);
    setPreferencesOpen(false);
  }, []);

  const value = useMemo<Ctx>(() => ({
    consent,
    hasDecided,
    isPreferencesOpen,
    openPreferences: () => setPreferencesOpen(true),
    closePreferences: () => setPreferencesOpen(false),
    acceptAll: () => commit({ necessary: true, analytics: true, marketing: true }),
    rejectAll: () => commit({ necessary: true, analytics: false, marketing: false }),
    save: (partial) => commit({ ...consent, ...partial, necessary: true }),
  }), [consent, hasDecided, isPreferencesOpen, commit]);

  return (
    <CookieConsentContext.Provider value={value}>{children}</CookieConsentContext.Provider>
  );
}

export function useCookieConsent() {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) throw new Error("useCookieConsent must be used within CookieConsentProvider");
  return ctx;
}
